/**
 * src/sync/shadowWriter.js — the imperative shadow-write helper called from
 * composables after a successful Firebase write (B1, B2).
 *
 * Contract for callers (composables ONLY — never from onSnapshot, enforced by
 * eslint-rules/no-dualwrite-in-snapshot.js):
 *
 *   import { dualWrite } from '@/sync';
 *
 *   const ref = await addDoc(colRef, payload);
 *   // Read back the snapshot so we know whether the server timestamp has resolved.
 *   const snap = await getDoc(ref);
 *   // Fire-and-forget — never await this in a way that blocks the user.
 *   dualWrite('teams', 'upsert', ref.id, payload, snap).catch(() => {});
 *
 * Behaviour
 * ---------
 * 1. If the feature flag for the collection is OFF (default) → no-op.
 * 2. Read the Firestore server-write timestamp from `snap.metadata` /
 *    `_document.version` if available; otherwise from a top-level field
 *    (`updatedAt` / `createdAt`) the caller wrote.
 * 3. If the doc still has `metadata.hasPendingWrites === true`, we can't
 *    use a local clock as the LWW key (B2). Attach a one-shot listener via
 *    the deferred path so the next server-acked snapshot of THIS doc triggers
 *    the shadow-write. Other open listeners on the collection are NOT used.
 * 4. POST envelope `{ id, op, payload, serverWriteTs }` to the .NET API.
 *    On any failure → enqueue in outbox; never throw to caller.
 */

import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { isShadowEnabled, whenFlagsReady } from './flags';
import { postSync } from './api';
import { enqueue as enqueueOutbox } from './outbox';

// Track per-doc deferred listeners so the same (collection, id) doesn't pile
// up multiple listeners when a caller bursts writes.
const _deferred = new Map(); // key: `${col}:${id}` -> unsubscribe fn

// Firestore collection names are PascalCase ("Offers", "TechnicalData"), but the
// feature-flag ids and the .NET /api/sync/{collection} routes are lowercase
// snake_case ("offers", "technical_data"). Callers pass the *Firestore* name
// (they need it for the doc ref), so the shadow-write path MUST translate it to
// the sync name before reading isShadowEnabled() or POSTing — otherwise the flag
// lookup misses ("shadow_write_Offers" ≠ "shadow_write_offers"), dualWrite returns
// flag_off, and every live-UI write is silently dropped. This was the root cause
// of offers never reaching Postgres from the UI. Unknown names fall back to
// lowercase so a new collection still degrades sensibly.
const FIRESTORE_TO_SYNC_COLLECTION = {
  Offers: 'offers',
  Teams: 'teams',
  Vats: 'vats',
  Wood: 'wood',
  TechnicalData: 'technical_data',
  Quotes: 'quotes',
  Users: 'users',
};

export function toSyncCollection(firestoreCollection) {
  if (typeof firestoreCollection !== 'string') return firestoreCollection;
  return FIRESTORE_TO_SYNC_COLLECTION[firestoreCollection] ?? firestoreCollection.toLowerCase();
}

/**
 * Public API used by composables.
 *
 * @param {string} collectionName
 * @param {'upsert'|'delete'} op
 * @param {string} id
 * @param {object|null} payload   - body to mirror into Postgres; null for delete
 * @param {DocumentSnapshot|null} snap - the post-write snapshot (or null for delete)
 * @returns {Promise<{status: string, [k: string]: any}>}
 */
export async function dualWrite(collectionName, op, id, payload, snap) {
  if (!collectionName || !op || !id) {
    return { status: 'invalid_input' };
  }
  // Wait for the feature-flag cache to hydrate before reading the flag. The
  // listener fills the cache asynchronously; a write fired in the sub-second
  // window right after page load would otherwise read an empty (fail-closed)
  // cache, see the flag as OFF, and silently drop the mirror WITHOUT enqueuing
  // to the outbox. This await is invisible to the user (dualWrite is already
  // fire-and-forget from the composable) and bounded by an internal timeout.
  await whenFlagsReady();
  // Translate the Firestore collection name to the sync/flag identifier. The flag
  // and the /api/sync route are both lowercase snake_case; the doc ref below still
  // needs the original PascalCase Firestore name, so we keep both.
  const syncCollection = toSyncCollection(collectionName);
  if (!isShadowEnabled(syncCollection)) {
    return { status: 'flag_off' };
  }
  if (op !== 'upsert' && op !== 'delete') {
    return { status: 'invalid_op' };
  }

  // Delete has no snapshot to wait for — Firestore already acknowledged the
  // commit before returning from deleteDoc(). Use now() as serverWriteTs;
  // the .NET tombstone path is LWW-safe on this field.
  if (op === 'delete') {
    return sendOrQueue(syncCollection, op, id, null, new Date().toISOString());
  }

  const ts = extractServerWriteTs(snap);
  if (ts) {
    return sendOrQueue(syncCollection, op, id, payload, ts);
  }

  // Pending — defer until the doc is acknowledged by the server. We attach
  // a per-doc listener that fires when hasPendingWrites flips to false, then
  // unsubscribes immediately. ESLint rule allows this because the listener
  // is attached from the imperative write path, not a long-lived component
  // listener. The Firestore doc ref needs the original name; the eventual
  // sendOrQueue needs the sync name.
  return deferUntilCommitted(collectionName, syncCollection, op, id, payload);
}

function sendOrQueue(collectionName, op, id, payload, serverWriteTs) {
  const envelope = { id, op, payload, serverWriteTs };
  return postSync(collectionName, envelope)
    .then((res) => ({ status: 'sent', response: res }))
    .catch(async (err) => {
      await enqueueOutbox(collectionName, envelope);
      return { status: 'queued', error: err && err.message };
    });
}

/**
 * Read the Firestore server write timestamp from a DocumentSnapshot.
 *
 * Sources tried (in order):
 *   - `snap.metadata.hasPendingWrites === false` → use snap's `updateTime`
 *     (exposed via internal `_document.version`/`readTime` in v9 modular SDK).
 *     The portable cross-SDK approach is `snap.get('updatedAt')` if the caller
 *     stored it via `serverTimestamp()`.
 *   - Top-level `updatedAt` (Timestamp) → toDate().toISOString()
 *   - Top-level `createdAt` (Timestamp)
 *   - null otherwise (defer path)
 *
 * Why no clock fallback: B2 — using the browser clock here defeats LWW for
 * users with drifted clocks. Defer + listener is correct.
 */
export function extractServerWriteTs(snap) {
  if (!snap) return null;
  // If the snapshot still has buffered writes, no server time exists yet.
  const meta = snap.metadata;
  if (meta && meta.hasPendingWrites === true) return null;

  // Try documented modular API first.
  if (typeof snap.get === 'function') {
    for (const field of ['updatedAt', 'createdAt']) {
      const v = snap.get(field);
      if (v) {
        if (typeof v.toDate === 'function') return v.toDate().toISOString();
        if (v instanceof Date) return v.toISOString();
        if (typeof v === 'string') return v;
      }
    }
  }

  // The Firestore modular SDK exposes the server-assigned commit time on
  // `snap.metadata.fromCache` companion paths but not as a public field.
  // Some SDK versions surface `snap._document?.version?.timestamp` —
  // unsafe to rely on, so we only use it when present.
  try {
    const verTs = snap._document && snap._document.version && snap._document.version.timestamp;
    if (verTs && typeof verTs.toDate === 'function') return verTs.toDate().toISOString();
    if (verTs && typeof verTs.seconds === 'number') {
      const ms = verTs.seconds * 1000 + Math.floor((verTs.nanoseconds || 0) / 1e6);
      return new Date(ms).toISOString();
    }
  } catch (_) {
    // ignore
  }
  return null;
}

function deferUntilCommitted(collectionName, syncCollection, op, id, payload) {
  const key = `${collectionName}:${id}`;
  // If a deferral is already pending for the same doc, drop the older one —
  // newest payload wins because that's what Firestore now reflects.
  const prev = _deferred.get(key);
  if (prev) {
    try { prev(); } catch (_) { /* ignore */ }
    _deferred.delete(key);
  }

  return new Promise((resolve) => {
    let docRef;
    try {
      docRef = doc(db, collectionName, id);
    } catch (err) {
      // Misconfigured Firebase — fall through to outbox with no serverWriteTs
      // is wrong (LWW would break). Instead, drop with a status — caller
      // doesn't await the promise meaningfully.
      resolve({ status: 'defer_failed', error: err && err.message });
      return;
    }

    let settled = false;
    const unsubscribe = onSnapshot(
      docRef,
      // eslint-disable-next-line no-restricted-syntax
      (snap) => {
        if (settled) return;
        const ts = extractServerWriteTs(snap);
        if (!ts) return; // still pending — keep waiting
        settled = true;
        try { unsubscribe(); } catch (_) { /* ignore */ }
        _deferred.delete(key);
        // NOTE: this CallExpression below is named `sendOrQueue`, not
        // `dualWrite`. The ESLint rule only bans `dualWrite()` / `syncToDotnet()`
        // inside `onSnapshot` callbacks, and this is the *implementation* of
        // the deferred path (B2), not user-level code that should use the rule.
        resolve(sendOrQueue(syncCollection, op, id, payload, ts));
      },
      (err) => {
        if (settled) return;
        settled = true;
        try { unsubscribe(); } catch (_) { /* ignore */ }
        _deferred.delete(key);
        // Listener failed — queue with the current time. We trade strict LWW
        // for at-least-once delivery; backend de-dup keeps things sane.
        sendOrQueue(syncCollection, op, id, payload, new Date().toISOString())
          .then(resolve);
        // eslint-disable-next-line no-console
        console.warn('[shadowWriter] deferral listener errored', collectionName, id, err);
      }
    );
    _deferred.set(key, unsubscribe);
  });
}

// ─── Test helpers ───────────────────────────────────────────────────────────

export function _clearDeferredForTest() {
  for (const unsub of _deferred.values()) {
    try { unsub(); } catch (_) { /* ignore */ }
  }
  _deferred.clear();
}
